import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage } from "../api/client";
import type { TenantInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import {
  Field,
  FormError,
  isStrongPassword,
  PasswordInput,
  PasswordRequirements,
  SubmitButton,
} from "../components/FormControls";

export function ResetPasswordPage({ tenant }: { tenant: TenantInfo }) {
  const { token = "" } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!isStrongPassword(password) || password !== confirmation) {
      setError("Confira os requisitos e a confirmação da senha.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await auth.resetPassword(token, password);
      navigate("/login?reset=ok", { replace: true });
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Este link não é válido ou já foi utilizado."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      tenant={tenant}
      title="Defina uma nova senha"
      description="A nova senha será usada no próximo acesso ao portal."
    >
      <form className="space-y-4" onSubmit={submit}>
        {error && <FormError message={error} />}
        <Field label="Nova senha">
          <PasswordInput
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <PasswordRequirements password={password} />
        <Field
          label="Confirme a nova senha"
          error={
            confirmation && confirmation !== password
              ? "As senhas não são iguais."
              : undefined
          }
        >
          <PasswordInput
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>
        <SubmitButton
          loading={loading}
          disabled={!isStrongPassword(password) || confirmation !== password}
        >
          Salvar nova senha
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
